import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "@app/@core-auth";
import { AdminRestaurant, DeliveryLocation, Company, GetDefaultAdminRestaurantWithDetailsGQL } from "@app/@graphql";
import { environment } from "@src/environments";
import moment from "moment";
import { AsyncSubject, BehaviorSubject } from "rxjs";
import { Observable } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";
import { sortByKey } from "../utils";


@Injectable({
    providedIn: 'root',
})
export class RestaurantAdminService {
    private _restaurantData$: BehaviorSubject<AdminRestaurant>;
    private _companies$: BehaviorSubject<Company[]>;
    private _deliveryLocations$: BehaviorSubject<DeliveryLocation[]>;
    private ready$ = new AsyncSubject<boolean>();

    private _defaultRestaurantId: number;
    private _defaultRestaurantName: string;
    private _defaultLogoUrl: string;

    readonly _currentLanguage = "pl";
    
    constructor(
      private auth: AuthService,
      private defaultRestaurantQuery: GetDefaultAdminRestaurantWithDetailsGQL, 
      private http: HttpClient )
    {
        this._restaurantData$ = new BehaviorSubject<AdminRestaurant>(null);
        this._companies$ = new BehaviorSubject<Company[]>([]);
        this._deliveryLocations$ = new BehaviorSubject<DeliveryLocation[]>([]);

        if (this.auth.getIsRestaurantAdmin()) {
            this.getDefaultRestaurantDetails();
        }
    }

    public get DefaultRestaurantId(): number
    {
        return this._defaultRestaurantId;
    }

    public get DefaultName(): string
    {
        return this._defaultRestaurantName;
    }

    public get DefaultLogoUrl(): string
    {
        return this._defaultLogoUrl;
    }

    private getDefaultRestaurantDetails() {
        this.defaultRestaurantQuery.fetch().pipe(
            map(result => result.data),
            map(result => result.defaultRestaurantWithDetails),
            tap(result => {
                this._defaultRestaurantId = result.id;
                this._defaultRestaurantName = result.details.find(x=>x!==undefined).name;
                this._defaultLogoUrl = result.details.find(x=>x!==undefined).imageUrl;
                this._restaurantData$.next(result);
                this._companies$.next(result.companies);
                this._deliveryLocations$.next(result.deliveryLocations);
                this.ready$.next(true);
                this.ready$.complete();
            })
        ).subscribe();
    }

    public selectCompanies(): Observable<any[]> {
        return this._companies$.asObservable().pipe(
            map(results => results.map( (item) => {
                return {
                    id: item.id, 
                    name: item.details.find(i => i.language.code == this._currentLanguage).name
                }
            } )),
            sortByKey('name')
        );
    }

    public selectDevlieryLocations(): Observable<any[]> {
        return this._deliveryLocations$.asObservable().pipe(
            map(results => results.map( (item) => {
                return {
                    id: item.id, 
                    name: item.details.find(i => i.language.code == this._currentLanguage).name
                }
            } )),
            sortByKey('name')
        );
    }

    public selectAvailableTimeslots(): Observable<any[]> {
        return this._deliveryLocations$.asObservable().pipe(
          map(results => results.map( (item) => {
              return item.timeSlots?.map( (timeslot) => {
                  return {
                    id: timeslot.id,
                    hour: timeslot.hour,
                    minute: timeslot.minute,
                    humanReadable: timeslot.hour.toString().padStart(2, '0') + ':' + timeslot.minute.toString().padStart(2,'0')
                  }
              });
          } ).flat())
        );
    }

    private download(url: string, params: any): Observable<Blob> {
        return this.http.get(environment.apiUrl + url, {
            responseType: 'blob',
            params: params
        })
      }

    private downloadPost(url: string, params: any): Observable<Blob> {
      return this.http.post(environment.apiUrl + url, params, {
        responseType: 'blob'
      }) 
    }

    public downloadStickersPDF(selectedIds: number[], languageCode: string): void {
      this
        .download('restaurant_export/printStickers', 
          {
            restaurantId: '' + this.DefaultRestaurantId,
            languageCode: languageCode,
            selectedIds: selectedIds.map(a => a.toString())
          }
        )
        .subscribe(blob => {
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = `${this._defaultRestaurantName}_${moment().unix()}.pdf`;
          a.click();
          URL.revokeObjectURL(objectUrl);
        })
    }

    public confirmOrdersAndDownloadStickersPDF(selectedIds: number[], languageCode: string, justRealize: boolean): void {
        this
          .downloadPost('restaurant_export/giveOutAndMaybePrintStickers', 
            {
              restaurantId: '' + this.DefaultRestaurantId,
              languageCode: languageCode,
              selectedIds: selectedIds.map(a => a.toString()),
              justRealize: justRealize
            }
          )
          .subscribe(blob => {
            if (!justRealize) {
              const a = document.createElement('a');
              const objectUrl = URL.createObjectURL(blob);
              a.href = objectUrl;
              a.download = `${this._defaultRestaurantName}_${moment().unix()}.pdf`;
              a.click();
              URL.revokeObjectURL(objectUrl);
            }
          })
      }

      public downloadStatementPDF(start: Date, end: Date, languageCode: string): void {
        this
          .download('restaurant_export/ordersPdf', 
            {
              restaurantId: '' + this.DefaultRestaurantId,
              start: moment(start).toISOString(),
              end: moment(end).toISOString(),
              languageCode: languageCode
            }
          )
          .subscribe(blob => {
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            a.download = `${this._defaultRestaurantName}_${moment().unix()}.pdf`;
            a.click();
            URL.revokeObjectURL(objectUrl);
          })
      }

      ready(): Observable<boolean> {
        return this.ready$.asObservable().pipe(shareReplay());
      }
}